import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { StripedWordmark, TricolorStrip } from '../components/BSCAuthLayout';
import { Package, Building2, User, Calendar, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  active: { bg: '#E8F5E9', color: '#346639', label: 'Active' },
  disposed: { bg: '#FEE', color: '#A43E33', label: 'Disposed' },
  'in-repair': { bg: '#FFF8E1', color: '#C77D38', label: 'In Repair' },
};

const CATEGORY_LABEL = {
  hard: 'Hard (physical)',
  soft: 'Soft (software / digital)',
};

const SPACE_TYPE_LABEL = {
  showroom: 'Showroom Space',
  digital: 'Digital Space',
};

/**
 * Public asset scan landing page — reachable from a printed QR sticker.
 * Does NOT require login. Only exposes safe, non-financial fields:
 * code, name, description, category, status, purchase date,
 * assigned location (sans value) and staff (sans contact details).
 */
export default function AssetScan() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/assets/public/${code}`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Asset not found');
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F7F5F0' }}>
      <div className="border-b" style={{ backgroundColor: '#0A0A0A', borderColor: '#1a1a1a' }}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/bsc-logo.jpeg" alt="BSC" className="w-8 h-8 rounded-lg" />
            <StripedWordmark text="ASSETS" size="sm" />
          </div>
          <Link to="/login" className="text-white/70 text-sm hover:text-white">Staff Login →</Link>
        </div>
        <TricolorStrip />
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10 w-full">
        {error && (
          <div className="bg-white rounded-lg border p-8 text-center" style={{ borderColor: '#E8E5DF' }} data-testid="asset-not-found">
            <AlertCircle size={48} className="mx-auto mb-3" style={{ color: '#A43E33' }} />
            <h2 className="text-xl font-semibold" style={{ color: '#1A1A1A' }}>Asset not found</h2>
            <p className="text-sm mt-1" style={{ color: '#5C5C5C' }}>The QR you scanned does not match any asset in the system. Code: <span className="font-mono">{code}</span></p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E8E5DF' }} data-testid="asset-info">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded" style={{ backgroundColor: '#0A0A0A' }}>
                  <Package size={28} color="#FFF" />
                </div>
                <div className="flex-1">
                  <div className="font-mono text-sm" style={{ color: '#5C5C5C' }}>{data.asset_code}</div>
                  <h1 className="text-2xl font-semibold leading-tight" style={{ fontFamily: 'Manrope', color: '#1A1A1A' }}>{data.name}</h1>
                  {data.description && <p className="text-sm mt-1" style={{ color: '#5C5C5C' }}>{data.description}</p>}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: STATUS_BADGE[data.status]?.bg, color: STATUS_BADGE[data.status]?.color }}>{STATUS_BADGE[data.status]?.label || data.status}</span>
                    {data.category && <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: '#F2EFEB', color: '#1A1A1A' }}>{CATEGORY_LABEL[data.category] || data.category}</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E8E5DF' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={16} style={{ color: '#5C5C5C' }} />
                  <h3 className="text-xs uppercase tracking-wider" style={{ color: '#5C5C5C' }}>Location</h3>
                </div>
                {data.store ? (
                  <>
                    <div className="font-medium" style={{ color: '#1A1A1A' }}>{data.store.name}</div>
                    <div className="text-xs mt-1" style={{ color: '#5C5C5C' }}>{SPACE_TYPE_LABEL[data.store.space_type] || data.store.space_type}{data.store.location ? ` · ${data.store.location}` : ''}</div>
                  </>
                ) : <div className="text-sm" style={{ color: '#5C5C5C' }}>Not assigned to a location</div>}
              </div>

              <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E8E5DF' }}>
                <div className="flex items-center gap-2 mb-2">
                  <User size={16} style={{ color: '#5C5C5C' }} />
                  <h3 className="text-xs uppercase tracking-wider" style={{ color: '#5C5C5C' }}>Assigned to</h3>
                </div>
                {data.staff ? (
                  <>
                    <div className="font-medium" style={{ color: '#1A1A1A' }}>{data.staff.name}</div>
                    <div className="text-xs mt-1" style={{ color: '#5C5C5C' }}>{data.staff.emp_id}{data.staff.designation ? ` · ${data.staff.designation}` : ''}</div>
                  </>
                ) : <div className="text-sm" style={{ color: '#5C5C5C' }}>Unassigned</div>}
              </div>

              {data.date_of_purchase && (
                <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E8E5DF' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} style={{ color: '#5C5C5C' }} />
                    <h3 className="text-xs uppercase tracking-wider" style={{ color: '#5C5C5C' }}>Purchase Date</h3>
                  </div>
                  <div className="font-medium" style={{ color: '#1A1A1A' }}>{data.date_of_purchase}</div>
                </div>
              )}
            </div>

            <div className="text-center text-xs" style={{ color: '#5C5C5C' }}>
              For full asset details (cost, bill, history) please <Link to="/login" className="underline">log in</Link>.
            </div>
          </div>
        )}

        {!error && !data && (
          <div className="py-20 text-center" style={{ color: '#5C5C5C' }}>Loading asset…</div>
        )}
      </div>
    </div>
  );
}
